import {
  css, type Interpolation,
} from '@emotion/react';
import styled from '@emotion/styled';
import {
  type JSX, type ReactNode,
} from 'react';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { stringify } from '~/_shared/utils/string/stringify';

export const radioIndicatorDim = 20;

export const RadioIndicator = styled.span<Readonly<{ isChecked: boolean }>>(({ isChecked, theme }) => ({
  boxSizing: 'border-box',
  display: 'block',
  width: radioIndicatorDim,
  height: radioIndicatorDim,
  borderRadius: '50%',
  border: isChecked ? `6px solid ${theme.radioColors.checkedBorder}` : `2px solid ${theme.radioColors.uncheckedBorder}`,
  background: isChecked ? theme.radioColors.checkedFill : theme.radioColors.uncheckedFill,
  transition: 'border .2s, background .2s',
  flexShrink: 0,
}));

const containerStyle = ({ isChecked, isDisabled }: Readonly<{
  isChecked: boolean;
  isDisabled?: boolean;
}>) => (theme: Theme) => {
  let radioIndicatorBackground = undefined;

  if (!isDisabled) {
    radioIndicatorBackground = isChecked
      ? theme.radioColors.checkedFill : theme.radioColors.checkedFillHover;
  }

  return css({
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    opacity: isDisabled ? theme.opacity.disabled : undefined,
    maxWidth: '100%',

    '&:hover': {
      [`${RadioIndicator}`]: {
        backgroundColor: radioIndicatorBackground,
      },
    },
  });
};

const radioInputStyle = css({
  display: 'none',
});

const iconWrapperStyle = ({ isNested }: { isNested?: boolean }) => (theme: Theme) => css({
  color: isNested ? theme.textColors.secondary : theme.radioColors.icon,
  margin: isNested ? '0 0 0 8px' : '0 15px 0 20px',
});

const labelStyle = (theme: Theme) => css({
  color: theme.radioColors.text,
  display: 'block',
});

const descriptionStyle = (theme: Theme) => css({
  color: theme.radioColors.text,
  display: 'block',
});

const textContainerStyle = ({ hasIcon }: { hasIcon: boolean }) => css({
  marginLeft: 8,
  maxWidth: `calc(100% - ${hasIcon ? 90 : 40}px)`,
});

export type RadioProps<T extends (string | null)> = Readonly<{
  className?: string;
  description?: ReactNode;
  icon?: ReactNode;
  isChecked: boolean;
  isDisabled?: boolean;
  isNested?: boolean;
  label?: ReactNode;
  ariaLabel?: string;
  tooltip?: string;
  value: T;

  onChange: (value: T) => void;
  radioIconStyle?: ((isActive: boolean) => Interpolation) | Interpolation;
  selectId?: (value: T) => string;
}>;

export const RadioComponent = <T extends string>({
  className,
  description,
  icon,
  isChecked,
  isDisabled,
  isNested,
  label,
  onChange,
  radioIconStyle,
  selectId = stringify,
  tooltip,
  value,
  ariaLabel,
  ...restProps
}: RadioProps<T> & Omit<JSX.IntrinsicElements['label'], keyof RadioProps<T>>) => {

  const labelElement = (
    <label
      {...restProps}
      className={className}
      css={containerStyle({ isChecked, isDisabled })}
    >
      <input
        css={radioInputStyle}
        type="radio"
        disabled={isDisabled}
        checked={isChecked}
        onChange={e => {
          onChange(e.target.value as T);
        }}
        value={selectId(value)}
        aria-label={ariaLabel}
      />
      <RadioIndicator
        isChecked={isChecked}
        css={(typeof radioIconStyle === 'function') ? radioIconStyle?.(isChecked) : radioIconStyle}
      />

      {icon && (
        <span css={iconWrapperStyle({ isNested })}>
          {icon}
        </span>
      )}

      {(label || description) && (
        <div css={textContainerStyle({ hasIcon: !!icon })}>
          <span css={labelStyle}>
            {label}
          </span>
          <span css={descriptionStyle}>
            {description}
          </span>
        </div>
      )}
    </label>
  );

  return tooltip ? (
    <TooltipComponent tooltipContent={tooltip}>
      {labelElement}
    </TooltipComponent>
  ) : labelElement;
};
